/*
 |------------------------------------------------------------------------------
 | DOM Utility
 |------------------------------------------------------------------------------
 |
 | A set of helper functions to make it easier to perform common actions
 | that needed to be done with the DOM.
 |
 |*/

/* ========================================================================== *\
	Imports
\* ========================================================================== */

import { assignIn, isFunction, isElement, uniqueId, isObject } from 'lodash';

/* ========================================================================== *\
	Functions
\* ========================================================================== */

/**
 * Gets the right type of matches method based on vendor
 * for a provided element passed in
 */
function getVendorMatchesMethod(element) {
	return (
		element.matches ||
		element.webkitMatchesSelector ||
		element.mozMatchesSelector ||
		element.msMatchesSelector
	);
}

/**
 * Remove an element from the DOM
 */
function removeElement(element) {
	if (isElement(element) && isElement(element.parentElement)) {
		element.parentElement.removeChild(element);
	}
}

/**
 * Returns the ID for the provided element. If the element doesn't yet have
 * an ID assigned to it, the method will generate a unique ID and assign it
 * to the element before returning the ID.
 *
 * @param {NodeItem} element    The element for which the ID should
 *        be returned.
 * @param {String}      idPrefix   Optional param to specificy the start of
 *        the ID in case it needs to be generated.
 *
 * @returns {String} Returns the ID which identifies the element in the DOM.
 */
function getIdForElement(element, idPrefix = 'rnd') {
	if (element == null) {
		return null;
	}

	// If the element has an ID we can simply return it.
	if (element.id !== '') {
		return element.id;
	}

	// The element doesn't yet have an ID, generate one for the element and
	// return it after assigning it to the element.
	element.id = uniqueId(idPrefix);

	return element.id;
}

/**
 * Finds an element in the start element ancestors tree.
 *
 * @param  {NodeItem} startElement The start element for the search. The
 *         start element will also be evaluated to see if it matches the
 *         predicate.
 * @param  {Function}    predicate    A method which accepts a single param,
 *         an instance of NodeItem. The method should return true when the
 *         element matches the search criteria.
 * @param  {NodeItem} endElement   Optional param, this can be an element
 *         in the ancestor tree at which the search will stop. When the param
 *         is null or the element is not an ancestor of the start element,
 *         the search will stop as soon as we have reached the body element.
 *         The end (or body) element is included in the evaluation for a
 *         matching ancestor element.
 *
 * @return {NodeItem} The element in the start element ancestor tree
 *         matching the predicate. When there is no element in the ancestor
 *         tree matching the predicate the result will be null.
 */
function findAncestor(startElement, predicate, endElement) {
	do {
		if (!isElement(startElement) || !isFunction(predicate)) {
			break;
		}

		// Check if the current element matches the predicate, if it does we
		// will return the current element as the method result.
		if (predicate(startElement)) {
			return startElement;
		}

		// No match yet, go the parent node for the next iteration.
		startElement = startElement.parentNode;
	} while (
		// Keep traversing the ancestor tree until one of the following two
		// condition is true:
		// a: The current element is the provided end element OR
		// b: The current element is the body element
		startElement !== endElement && // [a]
		startElement !== document.body // [b]
	);

	// Check if the end element matches the predicate, it it does we will
	// return it as the method result.
	if (predicate(startElement)) {
		return startElement;
	}

	// No matching element found.
	return null;
}

/**
 * Query ancestor tree for a specific selector
 *
 * @param  {DomItem} element
 * @param  {String} selector
 */
function queryAncestor(element, selector) {
	const matches = getVendorMatchesMethod(element);

	return findAncestor(element, el => matches.call(el, selector));
}

/**
 * Create an element instance from a HTML string
 *
 * @param {String} html
 * @returns {Element}
 */
function createElementFromHTML(html) {
	const tempDiv = document.createElement('div');

	tempDiv.innerHTML = html;

	return tempDiv.children[0];
}

/**
 * Prepend element in parent element
 *
 * @param {Element} parentElement
 * @param {Element} element
 */
function prependChild(parentElement, element) {
	parentElement.insertBefore(element, parentElement.childNodes[0]);
}

/**
 * Get an live set of data - meaning that if the set changes
 * the data will also change accordingly
 *
 * @param {Element} element
 * @param {String} key
 * @returns {*}
 */
function getData(element, key) {
	if (isObject(element.customData) && element.customData[key]) {
		return element.customData[key];
	}

	return {};
}

/**
 * Set an live set of data - meaning that if the set changes
 * the data will also change accordingly
 *
 * @param {Element} element
 * @param {String} key
 * @param {*} data
 */
function setData(element, key, data) {
	if (!isObject(element.customData)) {
		element.customData = {};
	}

	assignIn(element.customData, {
		[key]: data
	});

	return data;
}

/**
 *
 * @param {*} element
 */
function isHidden(element) {
	const style = window.getComputedStyle(element);

	return style.display === 'none' || style.visibility === 'hidden';
}

/* ========================================================================== *\
	Exports
\* ========================================================================== */

export {
	createElementFromHTML,
	findAncestor,
	getData,
	getIdForElement,
	isHidden,
	prependChild,
	queryAncestor,
	removeElement,
	setData
};
